import { startOfDay, subDays, subWeeks, subMonths } from 'date-fns';
import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import { smoothReorder } from '@/common/utils/animations';
import handleException from '@/common/utils/errors';
import { showErrorToast } from '@/common/utils/toast';

export type Priority = 0 | 1 | 2;
export type ViewMode = 'daily' | 'weekly' | 'monthly';

export interface ChecklistItem {
  id: string;
  content: string;
  completed: boolean;
  created_at: string;
  completed_at: string | null;
  priority: Priority;
  widget_id: string;
  parent_id: string | null;
}

// Helper type for displaying items with their subitems
export interface ChecklistItemWithSubitems extends ChecklistItem {
  subitems?: ChecklistItem[];
}

interface ChecklistStore {
  items: ChecklistItem[];
  itemsWithSubitems: ChecklistItemWithSubitems[];
  loading: boolean;
  reorderTimeoutId: number | null;
  viewMode: ViewMode;
  setReorderTimeoutId: (id: number | null) => void;
  fetchItems: (widgetId: string) => Promise<void>;
  addItem: (widgetId: string, content: string, parentId?: string | null) => Promise<void>;
  toggleItem: (id: string, completed: boolean) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  updateItem: (id: string, content: string) => Promise<void>;
  updatePriority: (id: string, priority: Priority) => Promise<void>;
  copyUnfinishedItems: (widgetId: string, items: ChecklistItem[]) => Promise<void>;
  copyingItems: boolean;
  setViewMode: (mode: ViewMode, userId?: string) => void;
  loadUserPreferences: (userId: string) => Promise<void>;
  saveUserPreferences: (userId: string, viewMode: ViewMode) => Promise<void>;
}

const getDateRangeStart = (viewMode: ViewMode): Date => {
  const now = new Date();
  switch (viewMode) {
    case 'daily':
      return subDays(startOfDay(now), 5);
    case 'weekly':
      return subWeeks(startOfDay(now), 4);
    case 'monthly':
      return subMonths(startOfDay(now), 3);
    default:
      return subDays(startOfDay(now), 5);
  }
};

export const useChecklistStore = create<ChecklistStore>((set, get) => ({
  items: [],
  itemsWithSubitems: [],
  loading: false,
  reorderTimeoutId: null,
  copyingItems: false,
  viewMode: 'daily',

  setReorderTimeoutId: (id: number | null) => set({ reorderTimeoutId: id }),

  saveUserPreferences: async (userId: string, viewMode: ViewMode) => {
    try {
      const { error } = await supabase.from('user_preferences').upsert(
        {
          user_id: userId,
          checklist_view_mode: viewMode,
        },
        { onConflict: 'user_id' },
      );

      if (error) {
        handleException(error);
      }
    } catch (error) {
      handleException(error);
    }
  },

  setViewMode: (mode: ViewMode, userId?: string) => {
    set({ viewMode: mode });
    if (userId) {
      get().saveUserPreferences(userId, mode);
    }
  },

  loadUserPreferences: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('checklist_view_mode')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          get().setViewMode('daily', userId);
        } else {
          handleException(error);
        }
        return;
      }

      if (data?.checklist_view_mode) {
        set({ viewMode: data.checklist_view_mode as ViewMode });
      }
    } catch (error) {
      handleException(error);
    }
  },

  fetchItems: async (widgetId: string) => {
    set({ loading: true });
    try {
      await withAuthUser(async (userId) => {
        const dateRangeStart = getDateRangeStart(get().viewMode);

        const { data, error } = await supabase
          .from('checklist_items')
          .select('*')
          .eq('user_id', userId)
          .eq('widget_id', widgetId)
          .gte('created_at', dateRangeStart.toISOString())
          .order('priority', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Organize items into a hierarchical structure
        const items = data || [];
        const parentItems = items.filter((item) => item.parent_id === null);
        const subItems = items.filter((item) => item.parent_id !== null);

        const itemsWithSubitems = parentItems.map((parent) => {
          const children = subItems.filter((item) => item.parent_id === parent.id);
          return {
            ...parent,
            subitems: children.length > 0 ? children : undefined,
          };
        });

        set({
          items: data || [],
          itemsWithSubitems,
        });
      });
    } catch (error) {
      handleException(error);
      showErrorToast(
        'Failed to load items',
        'Could not load your checklist items. Please try again.',
      );
    } finally {
      set({ loading: false });
    }
  },

  addItem: async (widgetId: string, content: string, parentId: string | null = null) => {
    const optimisticItem: ChecklistItem = {
      id: crypto.randomUUID(),
      content,
      completed: false,
      created_at: new Date().toISOString(),
      completed_at: null,
      priority: 0,
      widget_id: widgetId,
      parent_id: parentId,
    };

    // Optimistically update UI
    set((state) => {
      // If it's a regular item (not a subitem)
      if (!parentId) {
        return {
          items: [optimisticItem, ...state.items],
          itemsWithSubitems: [
            { ...optimisticItem, subitems: undefined },
            ...state.itemsWithSubitems,
          ],
        };
      }

      // If it's a subitem
      const updatedItems = [optimisticItem, ...state.items];
      const updatedItemsWithSubitems = state.itemsWithSubitems.map((item) => {
        if (item.id === parentId) {
          // Add the new subitem to the parent
          const updatedSubitems = item.subitems
            ? [optimisticItem, ...item.subitems]
            : [optimisticItem];
          return { ...item, subitems: updatedSubitems };
        }
        return item;
      });

      return {
        items: updatedItems,
        itemsWithSubitems: updatedItemsWithSubitems,
      };
    });

    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('checklist_items')
          .insert([
            {
              content,
              user_id: userId,
              widget_id: widgetId,
              parent_id: parentId,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        if (data) {
          set((state) => {
            // Update items array
            const updatedItems = state.items.map((item) =>
              item.id === optimisticItem.id ? data : item,
            );

            // If it's a regular item (not a subitem)
            if (!parentId) {
              const updatedItemsWithSubitems = state.itemsWithSubitems.map((item) =>
                item.id === optimisticItem.id ? { ...data, subitems: undefined } : item,
              );
              return { items: updatedItems, itemsWithSubitems: updatedItemsWithSubitems };
            }

            // If it's a subitem
            const updatedItemsWithSubitems = state.itemsWithSubitems.map((item) => {
              if (item.id === parentId && item.subitems) {
                // Update the subitem in the parent's subitems array
                const updatedSubitems = item.subitems.map((subitem) =>
                  subitem.id === optimisticItem.id ? data : subitem,
                );
                return { ...item, subitems: updatedSubitems };
              }
              return item;
            });

            return { items: updatedItems, itemsWithSubitems: updatedItemsWithSubitems };
          });
        }
      });
    } catch (error) {
      set((state) => ({
        items: state.items.filter((item) => item.id !== optimisticItem.id),
        itemsWithSubitems: !parentId
          ? state.itemsWithSubitems.filter((item) => item.id !== optimisticItem.id)
          : state.itemsWithSubitems.map((item) => {
              if (item.id === parentId && item.subitems) {
                return {
                  ...item,
                  subitems: item.subitems.filter((subitem) => subitem.id !== optimisticItem.id),
                };
              }
              return item;
            }),
      }));
      handleException(error);
      showErrorToast('Failed to add item', 'The item could not be added. Please try again.');
    }
  },

  toggleItem: async (id: string, completed: boolean) => {
    const previousItems = get().items;
    const previousItemsWithSubitems = get().itemsWithSubitems;
    const completed_at = completed ? new Date().toISOString() : null;

    // Optimistically update the UI
    set((state) => {
      // Update items array
      const updatedItems = state.items.map((item) =>
        item.id === id ? { ...item, completed, completed_at } : item,
      );

      // Update itemsWithSubitems array
      const updatedItemsWithSubitems = state.itemsWithSubitems.map((item) => {
        // If this is the item being toggled
        if (item.id === id) {
          return { ...item, completed, completed_at };
        }

        // Check if the toggled item is a subitem of this item
        if (item.subitems) {
          const updatedSubitems = item.subitems.map((subitem) =>
            subitem.id === id ? { ...subitem, completed, completed_at } : subitem,
          );
          return { ...item, subitems: updatedSubitems };
        }

        return item;
      });

      return {
        items: updatedItems,
        itemsWithSubitems: updatedItemsWithSubitems,
      };
    });

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('checklist_items')
          .update({ completed, completed_at })
          .eq('id', id)
          .eq('user_id', userId);

        if (error) throw error;
      });
    } catch (error) {
      // Revert to previous state if there's an error
      set({
        items: previousItems,
        itemsWithSubitems: previousItemsWithSubitems,
      });
      handleException(error);
      showErrorToast(
        'Failed to update item',
        'The item status could not be updated. Please try again.',
      );
    }
  },

  deleteItem: async (id: string) => {
    const previousItems = get().items;
    const previousItemsWithSubitems = get().itemsWithSubitems;

    // Find if the item is a parent or a subitem
    const item = previousItems.find((item) => item.id === id);
    const isSubitem = item?.parent_id !== null;

    // Optimistically update the UI
    set((state) => {
      // Update items array - remove the item
      const updatedItems = state.items.filter((item) => item.id !== id);

      // Update itemsWithSubitems array
      let updatedItemsWithSubitems;

      if (isSubitem) {
        // If it's a subitem, remove it from its parent's subitems
        updatedItemsWithSubitems = state.itemsWithSubitems.map((item) => {
          if (item.subitems) {
            return {
              ...item,
              subitems: item.subitems.filter((subitem) => subitem.id !== id),
            };
          }
          return item;
        });
      } else {
        // If it's a parent item, remove it from the array
        updatedItemsWithSubitems = state.itemsWithSubitems.filter((item) => item.id !== id);
      }

      return {
        items: updatedItems,
        itemsWithSubitems: updatedItemsWithSubitems,
      };
    });

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('checklist_items')
          .delete()
          .eq('id', id)
          .eq('user_id', userId);

        if (error) throw error;
      });
    } catch (error) {
      // Revert to previous state if there's an error
      set({
        items: previousItems,
        itemsWithSubitems: previousItemsWithSubitems,
      });
      handleException(error);
      showErrorToast('Failed to delete item', 'The item could not be deleted. Please try again.');
    }
  },

  updateItem: async (id: string, content: string) => {
    const previousItems = get().items;
    const previousItemsWithSubitems = get().itemsWithSubitems;

    // Optimistically update the UI
    set((state) => {
      // Update items array
      const updatedItems = state.items.map((item) =>
        item.id === id ? { ...item, content } : item,
      );

      // Update itemsWithSubitems array
      const updatedItemsWithSubitems = state.itemsWithSubitems.map((item) => {
        // If this is the item being updated
        if (item.id === id) {
          return { ...item, content };
        }

        // Check if the updated item is a subitem of this item
        if (item.subitems) {
          const updatedSubitems = item.subitems.map((subitem) =>
            subitem.id === id ? { ...subitem, content } : subitem,
          );
          return { ...item, subitems: updatedSubitems };
        }

        return item;
      });

      return {
        items: updatedItems,
        itemsWithSubitems: updatedItemsWithSubitems,
      };
    });

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('checklist_items')
          .update({ content })
          .eq('id', id)
          .eq('user_id', userId);

        if (error) throw error;
      });
    } catch (error) {
      // Revert to previous state if there's an error
      set({
        items: previousItems,
        itemsWithSubitems: previousItemsWithSubitems,
      });
      handleException(error);
      showErrorToast('Failed to update item', 'The item could not be updated. Please try again.');
    }
  },

  updatePriority: async (id: string, priority: Priority) => {
    const previousItems = get().items;
    const previousItemsWithSubitems = get().itemsWithSubitems;
    const previousTimeoutId = get().reorderTimeoutId;

    // Clear any existing timeout
    if (previousTimeoutId) {
      window.clearTimeout(previousTimeoutId);
      get().setReorderTimeoutId(null);
    }

    // Optimistically update the UI without reordering
    set((state) => {
      // Update items array
      const updatedItems = state.items.map((item) =>
        item.id === id ? { ...item, priority } : item,
      );

      // Update itemsWithSubitems array
      const updatedItemsWithSubitems = state.itemsWithSubitems.map((item) => {
        // If this is the item being updated
        if (item.id === id) {
          return { ...item, priority };
        }

        // Check if the updated item is a subitem of this item
        if (item.subitems) {
          const updatedSubitems = item.subitems.map((subitem) =>
            subitem.id === id ? { ...subitem, priority } : subitem,
          );
          return { ...item, subitems: updatedSubitems };
        }

        return item;
      });

      return {
        items: updatedItems,
        itemsWithSubitems: updatedItemsWithSubitems,
      };
    });

    // Set a timeout for reordering
    const timeoutId = window.setTimeout(() => {
      set((state) => {
        const sortedItems = smoothReorder(state.items, (item) => {
          const dateValue = new Date(item.created_at).getTime();
          const priorityValue = item.priority * 1000000; // Multiply to ensure priority takes precedence
          return dateValue + priorityValue;
        });

        // Also sort the itemsWithSubitems array
        const sortedItemsWithSubitems = smoothReorder(state.itemsWithSubitems, (item) => {
          const dateValue = new Date(item.created_at).getTime();
          const priorityValue = item.priority * 1000000;
          return dateValue + priorityValue;
        }).map((item) => {
          // Sort subitems if they exist
          if (item.subitems && item.subitems.length > 0) {
            return {
              ...item,
              subitems: smoothReorder(item.subitems, (subitem) => {
                const dateValue = new Date(subitem.created_at).getTime();
                const priorityValue = subitem.priority * 1000000;
                return dateValue + priorityValue;
              }),
            };
          }
          return item;
        });

        return {
          items: sortedItems,
          itemsWithSubitems: sortedItemsWithSubitems,
        };
      });
      get().setReorderTimeoutId(null);
    }, 300); // Reduced to 300ms for better UX

    get().setReorderTimeoutId(timeoutId);

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('checklist_items')
          .update({ priority })
          .eq('id', id)
          .eq('user_id', userId);

        if (error) throw error;
      });
    } catch (error) {
      // Revert to previous state if there's an error
      if (get().reorderTimeoutId) {
        window.clearTimeout(get().reorderTimeoutId as number);
        get().setReorderTimeoutId(null);
      }
      set({
        items: previousItems,
        itemsWithSubitems: previousItemsWithSubitems,
      });
      handleException(error);
      showErrorToast(
        'Failed to update priority',
        'The item priority could not be updated. Please try again.',
      );
    }
  },

  copyUnfinishedItems: async (widgetId: string, items: ChecklistItem[]) => {
    const unfinishedItems = items.filter((item) => !item.completed);

    set({ copyingItems: true });
    try {
      await withAuthUser(async (userId) => {
        const newItems = unfinishedItems.map((item) => ({
          content: item.content,
          priority: item.priority,
          user_id: userId,
          widget_id: widgetId,
          parent_id: null,
        }));

        const { data, error } = await supabase.from('checklist_items').insert(newItems).select();

        if (error) throw error;
        if (data) {
          const newItemsWithSubitems: ChecklistItemWithSubitems[] = data.map((item) => ({
            ...item,
            subitems: undefined,
          }));

          set((state) => ({
            items: [...data, ...state.items],
            itemsWithSubitems: [...newItemsWithSubitems, ...state.itemsWithSubitems],
          }));
        }
      });
    } catch (error) {
      handleException(error);
      showErrorToast('Failed to copy items', 'The items could not be copied. Please try again.');
    } finally {
      set({ copyingItems: false });
    }
  },
}));
